"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { ArrowRight, Droplet, CircleDot } from "lucide-react";
import { cn } from "@/lib/utils";
import { trackEvent } from "@/lib/analytics";
import { promoHref, type PromoGlyph, type PromoOffer } from "@/lib/promotions";

export interface PromoCardCopy {
  eyebrow: string;
  headline: string;
  subhead: string;
  terms: string;
  cta: string;
  imageAlt: string;
  /** Rendered only when the deadline is near enough to create urgency. */
  endsLabel: string | null;
}

interface PromoCardProps {
  offer: PromoOffer;
  copy: PromoCardCopy;
  /** 1-based, for click analytics. */
  position: number;
  locale: string;
}

// Keyed by the PromoGlyph union, so adding a glyph to the model without a
// matching icon here is a compile error rather than a silent oil drop.
const GLYPHS: Record<PromoGlyph, typeof Droplet> = {
  oil: Droplet,
  tire: CircleDot,
};

/**
 * One promotional offer.
 *
 * Presentational: copy arrives resolved rather than looked up here, so the
 * card is testable without an i18n provider and a missing translation fails
 * at the single join point instead of deep inside a render tree.
 *
 * The CTA is styled as a link, not a button, on purpose. The hero carries
 * exactly one filled button ("Book Service"); offers are on-ramps to it, and
 * three equal-weight buttons above the fold is the choice paralysis the
 * single-CTA guidance warns about.
 */
export function PromoCard({ offer, copy, position, locale }: PromoCardProps) {
  const [artworkFailed, setArtworkFailed] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const Glyph = GLYPHS[offer.glyph];

  // `onError` alone cannot hold the never-collapse invariant. This card is
  // server-rendered, so the browser starts fetching the artwork from the
  // initial HTML — a 404 (or an optimizer error) fires `error` before React
  // hydrates and attaches the handler, and the event never fires again. The
  // visitor would keep a broken-image box in the hero, which is the exact
  // failure the fallback exists to prevent. A finished-but-zero-width image
  // is that miss, detected after the fact.
  useEffect(() => {
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth === 0) setArtworkFailed(true);
  }, []);

  return (
    <Link
      href={promoHref(offer)}
      onClick={() =>
        trackEvent({
          event: "hero_offer_click",
          offerId: offer.id,
          position,
          locale,
        })
      }
      className={cn(
        "group relative flex min-h-[132px] gap-4 overflow-hidden rounded-[20px]",
        "border border-white/10 bg-site-card p-4 transition-colors duration-200 motion-reduce:transition-none",
        "hover:border-yellow-500/40 hover:bg-site-section",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-yellow-500",
      )}
    >
      {/* Accent edge — brightens on hover to signal the whole card is the target */}
      <span
        aria-hidden="true"
        className="absolute inset-y-[14px] left-0 w-[3px] rounded-r-[3px] bg-yellow-500 opacity-60 transition-opacity group-hover:opacity-100 motion-reduce:transition-none"
      />

      <div className="relative w-[74px] flex-none overflow-hidden rounded-xl bg-neutral-800 sm:w-24">
        {artworkFailed ? (
          <span
            data-testid="promo-card-fallback"
            className="grid h-full w-full place-items-center bg-gradient-to-br from-yellow-500 to-yellow-600"
          >
            <Glyph className="h-8 w-8 text-black" aria-hidden="true" />
          </span>
        ) : (
          <Image
            ref={imgRef}
            src={offer.image}
            alt={copy.imageAlt}
            fill
            // Matches the container's actual widths (w-[74px], sm:w-24) so a
            // phone does not download the 96px asset for a 74px slot.
            sizes="(min-width: 640px) 96px, 74px"
            className="object-cover"
            onError={() => setArtworkFailed(true)}
          />
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col justify-center">
        <p className="mb-1.5 flex flex-wrap items-center gap-2 text-[10.5px] font-bold uppercase tracking-[0.13em] text-yellow-500">
          {copy.eyebrow}
          {copy.endsLabel !== null && (
            <span className="font-medium tracking-[0.06em] text-white/40">
              {copy.endsLabel}
            </span>
          )}
        </p>
        <h2 className="mb-1 text-xl font-bold leading-tight tracking-tight text-white">
          {copy.headline}
        </h2>
        <p className="mb-2 text-sm leading-snug text-white/70">
          {copy.subhead}
        </p>
        <p className="mb-2.5 text-xs leading-snug text-white/35">
          {copy.terms}
        </p>
        <span className="mt-auto inline-flex items-center gap-2 text-sm font-semibold text-yellow-500">
          {copy.cta}
          <ArrowRight
            className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-[3px] motion-reduce:transition-none motion-reduce:group-hover:transform-none"
            aria-hidden="true"
          />
        </span>
      </div>
    </Link>
  );
}
